import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../api/api";
import { ROLES } from "../utils/roles";
import "../styles/LoginPage.css";

const LoginPage = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  /* ── Animated Background Particles ───────────────────────────────────── */
  useEffect(() => {
    const container = document.querySelector(".particles");
    if (!container) return;

    const particles = [];
    for (let i = 0; i < 30; i++) {
      const particle = document.createElement("div");
      particle.className = "particle";
      particle.style.left = `${Math.random() * 100}%`;
      particle.style.animationDuration = `${8 + Math.random() * 12}s`;
      particle.style.animationDelay = `${Math.random() * 8}s`;
      const size = 2 + Math.random() * 3;
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;
      container.appendChild(particle);
      particles.push(particle);
    }

    return () => {
      particles.forEach((p) => p.remove());
    };
  }, []);

  /* ── Handle Login ────────────────────────────────────────────────────── */
  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);

    // Clear any existing auth data
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("username");
    localStorage.removeItem("user_role");

    try {
      const loginRes = await api.post("/auth/token/", { username, password });
      const { access, refresh } = loginRes.data;

      localStorage.setItem("access_token", access);
      localStorage.setItem("refresh_token", refresh);

      const userRes = await api.get("/users/me/", {
        headers: { Authorization: `Bearer ${access}` },
      });
      const user = userRes.data;

      localStorage.setItem("username", user.username);
      localStorage.setItem("user_role", user.role);
      localStorage.setItem("user", JSON.stringify(user));

      Swal.fire({
        title: "Welcome Back",
        text: `Hello, ${user.username}! Redirecting...`,
        icon: "success",
        confirmButtonColor: "#a92f6f",
        timer: 1500,
        timerProgressBar: true,
        showConfirmButton: false,
      });

      setTimeout(() => {
        switch (user.role) {
          case ROLES.ADMIN:
            navigate("/admin");
            break;
          case ROLES.REQUESTER:
            navigate("/requester");
            break;
          case ROLES.APPROVER:
            navigate("/approver");
            break;
          case ROLES.FINANCE:
            navigate("/finance");
            break;
          case ROLES.STORES:
            navigate("/stores");
            break;
          default:
            navigate("/login");
        }
      }, 1500);
    } catch (err) {
      console.error("Login error:", err);
      Swal.fire({
        title: "Access Denied",
        text: err.response?.data?.detail || "Invalid credentials. Please try again.",
        icon: "error",
        confirmButtonColor: "#a92f6f",
      });
    } finally {
      setLoading(false);
    }
  }, [username, password, navigate]);

  /* ── Accent Cards Data ───────────────────────────────────────────────── */
  const accentCards = [
    {
      icon: "🌹",
      title: "Quality First",
      text: "Premium rose production with world-class standards and sustainable practices",
    },
    {
      icon: "🌍",
      title: "Global Reach",
      text: "Exporting excellence to markets across Europe and beyond",
    },
    {
      icon: "👥",
      title: "People Matter",
      text: "Empowering our team with cutting-edge digital tools",
    },
    {
      icon: "📊",
      title: "Smart Procurement",
      text: "Streamlined purchase request management for maximum efficiency",
    },
  ];

  return (
    <div className="login-page">
      {/* Background Orbs */}
      <div className="orb orb-1" />
      <div className="orb orb-2" />
      <div className="orb orb-3" />

      {/* Floating Particles */}
      <div className="particles" />

      {/* Left Accent Cards */}
      <div className="login-accents left">
        {accentCards.slice(0, 2).map((card, i) => (
          <div key={i} className="accent-card">
            <div className="accent-icon">{card.icon}</div>
            <div className="accent-title">{card.title}</div>
            <div className="accent-text">{card.text}</div>
          </div>
        ))}
      </div>

      {/* Right Accent Cards */}
      <div className="login-accents right">
        {accentCards.slice(2, 4).map((card, i) => (
          <div key={i} className="accent-card">
            <div className="accent-icon">{card.icon}</div>
            <div className="accent-title">{card.title}</div>
            <div className="accent-text">{card.text}</div>
          </div>
        ))}
      </div>

      {/* Main Login Card */}
      <div className="login-card">
        {/* Brand Header */}
        <div className="login-brand">
          <div className="login-logo-ring">
            <div className="login-logo-inner">🌹</div>
          </div>
          <h1>R-Requit</h1>
          <p className="tagline">Where People & Quality Count</p>
          <div className="divider" />
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="login-form">
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <div className="input-wrapper">
              <div className="input-glow" />
              <span className="input-icon">@</span>
              <input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                autoFocus
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <div className="input-glow" />
              <span className="input-icon">*</span>
              <input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
          </div>

          <button type="submit" disabled={loading}>
            {loading ? (
              <span className="loading-dots">
                <span />
                <span />
                <span />
              </span>
            ) : (
              <>Sign In →</>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="login-footer">
          <p>© {new Date().getFullYear()} Rift Valley Roses Ltd</p>
          <span className="company-name">All rights reserved</span>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
